import { Component, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DateFnsModule } from 'ngx-date-fns';
import { SESService } from '../../_services/ses.service';
import { AlertService } from '../../_services/alert.service';
import { AuthService } from '../../_services/auth.service';
import { EmailService } from '../../_services/email.service';
import { FirebaseService } from '../../_services/firebase.service';
import { FirestoreStoreService } from '../../_services/firestore-store.service';
import { StorageService } from '../../_services/storage.service';
import { EmailTemplatePreviewComponent } from '../emails/email-template-preview/email-template-preview.component';
import { JsonPreviewDialogComponent } from '../../shared/json-preview-dialog/json-preview-dialog.component';
import { sortLanguages } from '../../_utils/utils';
import { getLanguageName } from '../../_datas/languages.data';

export interface LanguageVersion {
  languageCode: string;
  fullName: string;
  createdTimestamp: Date | null;
  hasContent?: boolean;
}

export interface GroupedTemplate {
  baseName: string;
  languages: LanguageVersion[];
  publishedTimestamp: Date | null;
  publishedBy: string | undefined;
}

@Component({
  selector: 'app-published',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIcon, MatTooltip, MatProgressSpinner, DateFnsModule],
  templateUrl: './published.component.html',
  styleUrl: './published.component.scss',
})
export class PublishedComponent {
  private emailService = inject(EmailService);
  private sesService = inject(SESService);
  private dialog = inject(MatDialog);
  private alertService = inject(AlertService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  public fbs = inject(FirebaseService);
  private storageService = inject(StorageService);
  private store = inject(FirestoreStoreService);

  // Regular property for ngModel binding
  searchQueryValue = '';
  isRefreshing = signal(false);
  loadingJsonFor = signal<string | null>(null);

  // Loading state from EmailService - tracks initial fetch of AWS SES templates
  get isLoading(): boolean {
    return this.emailService.templatesLoading();
  }

  // Computed signal: groups templates by base name with multiple languages
  // ONLY shows templates that are properly published (exist in AWS SES AND have publishedSnapshot in Firestore)
  groupedTemplates = computed<GroupedTemplate[]>(() => {
    const rawTemplates = this.emailService.emailTemplates();
    const allEmails = this.store.activeEmails(); // Use activeEmails to get only valid, non-deleted templates

    // Group by base name
    const groupMap = new Map<string, GroupedTemplate>();

    for (const t of rawTemplates) {
      const baseName = this.extractBaseName(t.Name);
      const languageCode = this.extractLanguageCode(t.Name);

      // Find the corresponding email in Firestore
      const email = allEmails.find((e) => e.templateName === baseName);

      // CRITICAL FIX: Only show templates that have a publishedSnapshot in Firestore
      // This ensures we only display properly published templates, not orphaned AWS SES templates
      if (!email || !email.publishedSnapshot) {
        continue; // Skip this template if it doesn't have a published snapshot
      }

      if (!groupMap.has(baseName)) {
        groupMap.set(baseName, {
          baseName,
          languages: [],
          publishedTimestamp: email.publishedSnapshot.publishedAt,
          publishedBy: email.publishedSnapshot.publishedBy,
        });
      }

      const group = groupMap.get(baseName)!;
      // If this language template exists in AWS SES (rawTemplates), it HAS content
      // SES doesn't store empty templates - existence = content available
      // The fullName (e.g., "me-daily-autoRenewalAlert-de") is the actual SES template name
      group.languages.push({
        languageCode,
        fullName: t.Name,
        createdTimestamp: t.CreatedTimestamp,
        hasContent: true, // If it's in the SES list, it has content
      });

      // Keep the publishedTimestamp from publishedSnapshot (no need to update)
      // Since all language versions should have been published at the same time
    }

    // Sort languages: English first, then alphabetically
    for (const group of groupMap.values()) {
      group.languages = sortLanguages(group.languages);
    }

    // Sort grouped templates by publishedTimestamp descending (latest first)
    const sortedGroups = Array.from(groupMap.values()).sort((a, b) => {
      const dateA = a.publishedTimestamp ? new Date(a.publishedTimestamp).getTime() : 0;
      const dateB = b.publishedTimestamp ? new Date(b.publishedTimestamp).getTime() : 0;
      return dateB - dateA;
    });

    return sortedGroups;
  });

  // Computed: applies search filter to grouped templates
  get filteredTemplates(): GroupedTemplate[] {
    const allTemplates = this.groupedTemplates();
    const query = this.searchQueryValue.toLowerCase().trim();
    if (!query) {
      return allTemplates;
    }
    return allTemplates.filter((t) => t.baseName.toLowerCase().includes(query));
  }

  // Total template count (sum of all language versions)
  get totalTemplateCount(): number {
    return this.groupedTemplates().reduce(
      (sum, t) => sum + t.languages.length,
      0
    );
  }

  // Filtered template count (sum of language versions in filtered results)
  get filteredTemplateCount(): number {
    return this.filteredTemplates.reduce(
      (sum, t) => sum + t.languages.length,
      0
    );
  }


  /** Check if current user is admin (can delete published templates) */
  get isAdmin(): boolean {
    return this.authService.isAdmin;
  }

  private extractBaseName(name: string): string {
    if (!name) return '';
    const parts = name.split('-');
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart.length === 2 && parts.length > 1) {
      return parts.slice(0, -1).join('-');
    }
    return name;
  }

  private extractLanguageCode(name: string): string {
    if (!name) return 'en';
    const parts = name.split('-');
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart.length === 2 && parts.length > 1) {
      return lastPart;
    }
    return 'en';
  }

  /**
   * Check if a date is valid
   */
  isValidDate(date: Date | null): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  onSearchChange() {
    // Trigger change detection - getter will re-evaluate
  }

  clearSearch() {
    this.searchQueryValue = '';
  }

  async refreshTemplates() {
    this.isRefreshing.set(true);
    try {
      await this.emailService.refreshEmailTemplates();
    } finally {
      this.isRefreshing.set(false);
    }
  }

  /** Preview a specific language version of a template - fetches directly from AWS SES */
  async previewLanguage(template: GroupedTemplate, langVersion: LanguageVersion) {
    const previewLangCode = langVersion.languageCode;

    // Fetch the actual content from AWS SES using the full template name
    // langVersion.fullName is the actual SES template name (e.g., "me-daily-autoRenewalAlert-de")
    const sesTemplate = await this.sesService.getTemplateContent(langVersion.fullName);

    if (!sesTemplate) {
      this.dialog.open(EmailTemplatePreviewComponent, {
        width: '100%',
        maxWidth: '900px',
        height: '100%',
        maxHeight: '90vh',
        panelClass: 'email-preview-dialog-panel',
        data: {
          templateName: template.baseName,
          htmlContent: '',
          subject: '',
          templateNotFound: true,
        },
      });
      return;
    }

    // Get email from Firestore for additional metadata (templateVariables, publishedAt, etc.)
    const email = this.store
      .activeEmails()
      .find((e) => e.templateName === template.baseName);

    // Build ALL language options for test email dialog
    // All languages in the template.languages array exist in SES, so they all have content
    const languages = template.languages.map(lang => ({
      languageCode: lang.languageCode,
      languageName: getLanguageName(lang.languageCode),
      isValid: true,  // If it's in SES, it's valid
      hasContent: true  // If it's in SES, it has content
    }));

    this.dialog.open(EmailTemplatePreviewComponent, {
      width: '100%',
      maxWidth: '900px',
      height: '100%',
      maxHeight: '90vh',
      panelClass: 'email-preview-dialog-panel',
      data: {
        templateName: template.baseName,
        htmlPart: { [previewLangCode]: sesTemplate.HtmlPart || '' },
        subjectPart: { [previewLangCode]: sesTemplate.SubjectPart || '' },
        templateVariables: email?.publishedSnapshot?.templateVariables || email?.templateVariables || [],
        defaultLangCode: previewLangCode,
        languageCodes: [previewLangCode],
        languages,
        defaultEmail: this.storageService.getUser()?.email || '',
        isPublishedVersion: true,
        publishedAt: email?.publishedSnapshot?.publishedAt,
        // Always use AWS SES template for published templates
        source: 'published' as const,
      },
    });
  }

  /** Copy template name (ID) to clipboard */
  async copyTemplateName(templateName: string, event: Event) {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(templateName);
      this.snackBar.open(`Copied "${templateName}" to clipboard`, 'OK', {
        duration: 2000,
      });
    } catch {
      this.snackBar.open('Failed to copy to clipboard', 'OK', {
        duration: 2000,
      });
    }
  }

  /** Delete all language versions of a template */
  async deleteTemplate(template: GroupedTemplate) {
    const languageCount = template.languages.length;
    const message =
      languageCount > 1
        ? `Are you sure you want to delete "${template.baseName}" and all ${languageCount} language versions?`
        : `Are you sure you want to delete "${template.baseName}"?`;

    const confirmed = await this.alertService.confirm(
      'Delete Template',
      message,
      'Delete',
      'Cancel'
    );
    if (confirmed) {
      // Delete all language versions
      for (const lang of template.languages) {
        await this.sesService.deleteSESTemplate(lang.fullName);
      }
      await this.emailService.refreshEmailTemplates();
    }
  }

  /** View template JSON structure */
  async viewTemplateJson(template: GroupedTemplate) {
    // Prevent multiple clicks by checking if already loading
    if (this.loadingJsonFor()) {
      return;
    }

    try {
      // Set loading state
      this.loadingJsonFor.set(template.baseName);

      // Fetch all language versions of the template from AWS SES
      const templateData: { [lang: string]: any } = {};

      for (const lang of template.languages) {
        const content = await this.sesService.getTemplateContent(lang.fullName);
        if (content) {
          templateData[lang.languageCode] = content;
        }
      }

      this.dialog.open(JsonPreviewDialogComponent, {
        width: '100%',
        maxWidth: '800px',
        height: '80vh',
        panelClass: 'json-preview-dialog-panel',
        data: {
          templateName: template.baseName,
          templateData,
        },
      });
    } finally {
      // Clear loading state
      this.loadingJsonFor.set(null);
    }
  }

  trackByBaseName(_index: number, template: GroupedTemplate): string {
    return template.baseName;
  }

  trackByLangCode(_index: number, lang: LanguageVersion): string {
    return lang.languageCode;
  }
}
